/**
 * Money in the award's own currency (grant awards wave, 2026-09-19).
 *
 * TRF awards in two currencies: a family in the US is paid in US dollars, a
 * family in Canada in Canadian dollars (contract C-1: `US → USD`, `CA → CAD`,
 * fixed by country). Every surface that prints an award amount goes through
 * `formatMoney` here, so a Canadian amount never reads as US dollars on one
 * screen and correctly on the next.
 *
 * One style, chosen once: Intl's own `en-US` rendering — "$1,000" for US
 * dollars and "CA$1,500" for Canadian dollars. The prefix is the currency, so
 * a mixed list (Awards across both countries) reads unambiguously without a
 * currency column. Whole dollars print without cents; cents print only where
 * the amount has them (guideline §5.5).
 *
 * Amounts are never summed across currencies — the Awards footer totals one
 * line per currency.
 */
import i18n from 'i18next';
import type { GrantCountry, GrantCurrency, GrantRequest, GrantUsage } from '@/types/api';
import { formatMoney as formatMoneyIntl, formatMonthYear } from '@/i18n/intl';
import { msg, resolveMsg, type Msg } from '@/i18n/msg';

export const COUNTRY_CURRENCY: Record<GrantCountry, GrantCurrency> = { US: 'USD', CA: 'CAD' };

export const COUNTRY_LABEL: Record<GrantCountry, string> = { US: 'United States', CA: 'Canada' };

/**
 * "$1,000" · "CA$1,500" · "$342.50" · "—" for no amount — in the reader's
 * locale since #2109 R3-B6 (the one Intl formatter in `i18n/intl.ts`; en is
 * byte-identical to the `en-US` rendering this used to hard-code).
 */
export function formatMoney(cents: number | null | undefined, currency: GrantCurrency = 'USD'): string {
  return formatMoneyIntl(cents, currency);
}

/** The currency a grant's amounts are in — the award's, else US dollars (pre-award rows). */
export function grantCurrency(grant: Pick<GrantRequest, 'award'>): GrantCurrency {
  return grant.award?.currency ?? 'USD';
}

/** 'YYYY-MM' → "October 2026". Null for anything that is not a month. */
export function monthLabel(ym: string | null | undefined): string | null {
  if (!ym) return null;
  const m = /^(\d{4})-(\d{2})$/.exec(ym);
  if (!m) return null;
  const month = Number(m[2]);
  if (month < 1 || month > 12) return null;
  return formatMonthYear(new Date(Date.UTC(Number(m[1]), month - 1, 1)), { dateOnly: true });
}

/** 'YYYY-MM' → "Oct 2026" — the short form a table cell uses. */
export function shortMonthLabel(ym: string | null | undefined): string | null {
  if (!ym) return null;
  const m = /^(\d{4})-(\d{2})$/.exec(ym);
  if (!m) return null;
  const month = Number(m[2]);
  if (month < 1 || month > 12) return null;
  return formatMonthYear(new Date(Date.UTC(Number(m[1]), month - 1, 1)), { dateOnly: true, month: 'short' });
}

/** The calendar month after `now`, as 'YYYY-MM' — the default first Signature payment. */
export function nextCalendarMonth(now: Date = new Date()): string {
  const d = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** 'YYYY-MM' plus n months (n may be negative). */
export function addMonthsYm(ym: string, n: number): string {
  const [y, m] = ym.split('-').map(Number);
  const idx = y * 12 + (m - 1) + n;
  return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, '0')}`;
}

/**
 * #2525: the first running Signature span a schedule of `months` months from
 * `startMonth` would share a month with, or null. Mirrors the server's
 * `signatureOverlap` (backend/src/models/GrantRequest.js), which refuses the
 * same schedule with a 409 — this only lets the dialog say so first.
 */
export function signatureSpanOverlap<T extends { first: string; last: string }>(
  spans: ReadonlyArray<T>,
  startMonth: string,
  months: number,
): T | null {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(startMonth)) return null;
  const last = addMonthsYm(startMonth, Math.max(1, months) - 1);
  return spans.find((s) => startMonth <= s.last && last >= s.first) ?? null;
}

/** Today in the reader's own calendar, as 'YYYY-MM-DD' (not UTC — a reader west of UTC would get tomorrow at night). */
export function todayIso(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

/** "$1,000 a month for 12 months from October 2026" — the Signature schedule in one phrase. */
export function signatureScheduleWords(
  monthlyCents: number,
  months: number,
  currency: GrantCurrency,
  startMonth?: string | null,
): string {
  const from = monthLabel(startMonth);
  const base = `${formatMoney(monthlyCents, currency)} a month for ${months} ${months === 1 ? 'month' : 'months'}`;
  return from ? `${base} from ${from}` : base;
}

/**
 * The line a parent reads under a Signature grant: "$1,000 a month · 3 of 12
 * paid". Null when the grant carries no monthly schedule.
 *
 * A descriptor (#2109 R3-B3): one key with the amount, the paid count and the
 * schedule length as params, and `count` = paid so a language can agree its
 * participle with the number. The `·` lives in the catalog.
 */
export function signaturePaidMsg(grant: Pick<GrantRequest, 'award'>): Msg | null {
  const award = grant.award;
  const schedule = award?.schedule;
  if (!award || !schedule) return null;
  // The parent read carries the count; the staff read carries the payments.
  const paid = schedule.paid_count ?? award.payments?.length ?? 0;
  return msg('grants:signature.paidLine', {
    amount: formatMoney(schedule.monthly_cents, award.currency),
    count: paid,
    months: schedule.months,
  });
}

/**
 * The Signature line on the FAMILY's grant row (D-2394 part B). While no
 * payment is recorded it names the start month instead of "0 of 12 paid":
 * "$1,000 a month · 12 months · starts November 2026". A new award's start
 * month otherwise showed nowhere, because the status line names only the
 * earliest month across all grants.
 *
 * A start already behind us reads "started December 2025", never "starts":
 * Bill.com payments are not recorded on the award (D-2394), so zero recorded
 * is unknown, and the row says only what it knows. With a payment recorded,
 * or no start month on file, it is `signaturePaidMsg` unchanged. Staff
 * readers keep `signaturePaidMsg`.
 */
export function signatureRowMsg(grant: Pick<GrantRequest, 'award'>, now: Date = new Date()): Msg | null {
  const award = grant.award;
  const schedule = award?.schedule;
  if (!award || !schedule) return null;
  const paid = schedule.paid_count ?? award.payments?.length ?? 0;
  const month = monthLabel(schedule.start_month);
  if (paid > 0 || !month || !schedule.start_month) return signaturePaidMsg(grant);
  const current = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  return msg(schedule.start_month >= current ? 'grants:signature.startsLine' : 'grants:signature.startedLine', {
    amount: formatMoney(schedule.monthly_cents, award.currency),
    count: schedule.months,
    month,
  });
}

/** R4 compatibility: the English sentence, for the staff FamilyProfilePage (deleted in R4). */
export function signaturePaidLine(grant: Pick<GrantRequest, 'award'>): string | null {
  const m = signaturePaidMsg(grant);
  return m ? resolveMsg(i18n.getFixedT('en'), m) : null;
}

/**
 * How a grant's remaining budget READS — one place, two renderers.
 *
 * `GrantRow` prints it on the line and `GrantUsageBar` prints it above the
 * bar. Before this (2026-09-21) each spelled "Over budget" itself, which is
 * the same thing said twice (§1.1) and two places to get a money sentence
 * wrong. It lives in `lib/` rather than beside either component so neither
 * owns the wording.
 *
 * Returns `null` when there is no budget to be consumed — an undecided grant
 * has no approved total, and `$0 left of $0` would be a false statement
 * about a family's money (§1.9). Callers render nothing on null rather than
 * substituting a zero.
 */
/**
 * A descriptor, not a sentence (#2109 R3-B3). `text` is the whole phrase —
 * `grants:remaining.overBudget`, or `grants:remaining.leftOf` whose value
 * wraps the two halves in `<amount>` / `<rest>` tags, so a renderer that
 * styles them (`GrantRow`) uses `<Trans>` with those two components and a
 * renderer that does not resolves it with `t()`. Word order is the
 * catalog's, never the code's.
 */
export type GrantRemaining =
  /** "Over budget". */
  | { overBudget: true; text: Msg; amount: null; approved: null }
  /** "$1,240 left of $3,000": the formatted remaining amount and approved total. */
  | { overBudget: false; text: Msg; amount: string; approved: string };

export function grantRemaining(
  usage: GrantUsage | null | undefined,
  currency: GrantCurrency = 'USD',
): GrantRemaining | null {
  if (!usage || typeof usage.approved_cents !== 'number') return null;
  if (usage.over_applied) {
    return { overBudget: true, text: msg('grants:remaining.overBudget'), amount: null, approved: null };
  }
  const amount = formatMoney(usage.remaining_cents, currency);
  const approved = formatMoney(usage.approved_cents, currency);
  return { overBudget: false, text: msg('grants:remaining.leftOf', { amount, approved }), amount, approved };
}
